import React from "react";
import { Box, FormHelperText, Modal, Paper, Stack } from "@mui/material";
import styled from "styled-components";
import request from "src/utils/request";
import { ToastContext } from "src/components/providers/toast-provider";
import AddToPlanTextfield from "src/scenes/subscription/add-to-plan-textfield";
import Button from "@mui/material/Button";

const ModalContainer = styled(Paper)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;

const Title = styled.h1`
    font-size: 18px;
    font-weight: 500;
    padding: 0;
    margin: 0;
`;

const SubTitle = styled.h2`
    font-size: 12px;
    font-weight: 300;
    padding: 0;
    margin: 0;
    color: ${({ theme }) => theme.palette.secondary.main};
    max-width: 28rem;
`;

const ResponseText = styled(FormHelperText)`
    color: ${({ theme }) => theme.palette.warning.main};
`;

const InvitationModal = ({ open, handleClose, subscription, planMembersApi }) => {
    const [response, setResponse] = React.useState({ error: undefined, message: undefined });
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const [usersToAdd, setUsersToAdd] = React.useState([]);
    const [validationBlocker, setValidationBlocker] = React.useState(false);
    const [addToPlanNotification, setAddToPlanNotification] = React.useState(false);

    const emailChecker = (input) => {
        const regexEmail =
            /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/gi;

        return regexEmail.test(input);
    };

    React.useEffect(() => {
        // eslint-disable-next-line array-callback-return
        usersToAdd.map((item) => {
            if (typeof item === "string") {
                const isEmail = emailChecker([item]);
                if (!isEmail) {
                    setValidationBlocker(true);
                } else {
                    setValidationBlocker(false);
                    setAddToPlanNotification(true);
                }
            } else {
                setValidationBlocker(false);
                setAddToPlanNotification(false);
            }
        });
    }, [usersToAdd]);

    const inviteMembersAction = async () => {
        const invitesToAdd = usersToAdd.reduce((acc, entry) => {
            if (typeof entry === "string") {
                acc.push(entry);
            } else if (typeof entry === "object" && entry.email) {
                acc.push(entry.email);
            }
            return acc;
        }, []);
        try {
            await request(`/api/invites/${subscription.id}/invite-plan`, {
                method: "POST",
                body: JSON.stringify({ emails: invitesToAdd }),
            });
            setResponse({ message: `Users successfully invited to Plan` });
            await planMembersApi.refetch();
        } catch (error) {
            setMessage(`There was an error inviting users to the plan.`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };

    const clearError = () => {
        setResponse({ error: undefined, message: undefined });
    };

    const closeModal = () => {
        setUsersToAdd([]);
        setResponse({ error: undefined, message: undefined });
        handleClose();
    };

    const handleSubmit = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        setIsToastTriggered(false);
        try {
            inviteMembersAction();
            setTimeout(closeModal, 1000);
        } catch (error) {
            setMessage(`There was an error adding the users to the plan`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };
    return (
        <Modal
            open={open}
            onClose={closeModal}
            aria-labelledby="team-member-add-modal-title"
            aria-describedby="team-member-add-modal-description"
        >
            <ModalContainer>
                <Box p={3}>
                    <form onSubmit={handleSubmit}>
                        <Stack spacing={2} alignItems="flex-start">
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                style={{ width: "100%" }}
                            >
                                <Title>Add & Invite users</Title>
                            </Stack>
                            <SubTitle>
                                Add users within your organisation automatically to your team or
                                invite and add new users.
                            </SubTitle>
                            <SubTitle>
                                Add users within your organisation automatically to your team or
                                invite and add new users.
                            </SubTitle>
                            <AddToPlanTextfield
                                usersToAdd={usersToAdd}
                                setUsersToAdd={setUsersToAdd}
                                clearError={clearError}
                            />
                            {addToPlanNotification && (
                                <ResponseText color="success">
                                    Invited users will be added to plan upon joining the platform
                                </ResponseText>
                            )}
                            <Button
                                variant="contained"
                                disabled={
                                    !usersToAdd || usersToAdd.length === 0 || validationBlocker
                                }
                                type="submit"
                            >
                                Add Users
                            </Button>
                            {validationBlocker && (
                                <ResponseText color="warning">Please add valid emails</ResponseText>
                            )}
                            {response && (
                                <ResponseText error={response.error} color="primary">
                                    {response.message}
                                </ResponseText>
                            )}
                        </Stack>
                    </form>
                </Box>
            </ModalContainer>
        </Modal>
    );
};

export default InvitationModal;
